import { memo, Reducer, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MainContentPaddingTypes, NewInput, NewModal, NewSelect, ToggleBox, TooltipString, WaitAnimation } from "../..";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { DeviceInfo, Graph_QL, SectionsInfo, useDeviceInfo, useSection, useTerminals } from "../../../Services";
import { useAppSelector } from "../../../Store";
import NewButton from "../../BaseComponents/newButton";
import { currentLfViewTypes, ILfContext, LfContext, LfDispatcherT, LfReducer, useLfContext } from "./_Modals/lfPenContext";
import LF_PENLIST from "../SubComponents/lfPenList";
import { ButtonTypes, NewFieldStyles, NewFieldTypes, NewInputHandles, NewSelectHandles } from "../../../Definitions";
import { ToggleStyles } from "../../BaseComponents/toggleBox";
import { RenameLocation, UpdateTerminal } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/createLfSectionCall";
import ConfirmModal from "../../_Components/confirmModal";

interface BaseIO {
  input: boolean;
  terminal_id: number;
  text: string;
  port: string | number;
  board: DeviceInfo | undefined;
  disabled: boolean;
  programmed: number;
}
interface PenDataProps {
  locationName: {
    text: string;
    value: string;
  };
  motor: BaseIO | undefined;
  waterOutput: BaseIO | undefined;
  activatorInput: BaseIO | undefined;
  visual: string | undefined;
  location_id: number;
}

interface NewSectionModal_Props {
  closeModal: () => void;
  section: SectionsInfo;
  focus_view?: boolean;
}

export default function EditSectionModal({ closeModal, section, focus_view = false }: NewSectionModal_Props) {
  const { t } = useTranslation();
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const { state, dispatch } = useLfContext();
  const { selectedPens, selectedSection, error } = state

  const sectionNameRef = useRef<NewInputHandles | null>(null);
  const penNameRefs = useRef<Record<number, NewInputHandles | null>>({});
  const portRefs = useRef<Record<number, NewInputHandles | null>>({});
  const boardRefs = useRef<Record<number, NewSelectHandles | null>>({});

  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;

  // Styles
  const _fontSize = { ...(focus_view ? fonts.fontSize3 : fonts.fontSize4) };
  
  // #region | GraphQL
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm })
    let connection = Graph_QL.create({ domain: farm })
    return { domain, connection }
  }, [farm]);

  const { call: getDevices, data: deviceInfoState } = useDeviceInfo(connection);
  const { call: getTerminals, data: terminalsState, } = useTerminals(connection);
  const { maintainSectionLocations, deleteSectionLocations } = useSection(connection)
  
  useEffect(() => {
    getDevices({ fetchAmount: 500 }, connection);
    getTerminals({});
  }, [connection, getDevices, getTerminals])

  const terminals = useMemo(() => terminalsState[domain] && terminalsState[domain].data, [domain, terminalsState])
  const devices = useMemo(() => deviceInfoState[domain] && deviceInfoState[domain].data, [deviceInfoState, domain])

  const ioOptions = devices?.map((type) => ({
    label: type.name,
    id: type.device_id
  }));
  // #endregion

  const terminalDirtRef = useRef<{[key:number]: UpdateTerminal}>({})
  const locationDirtRef = useRef<{[key: number]: RenameLocation}>({})
  const sectionChangeRef = useRef<string>();
  useEffect(() => {
    dispatch({ selectedSection: section, currentPenViewType: currentLfViewTypes.systemControle, selectedPens: []})
    const newSelectedPens = selectedPens === undefined ? undefined : section.section_locations.filter((location) => selectedPens?.map((pen) => pen.location_id).includes(location.location_id))
    if (newSelectedPens?.length) { dispatch({ selectedPens: newSelectedPens }) }
    
  },[section])

  // #region | Pen Data 
  const penData: PenDataProps[] = useMemo(() => {
    return Array.from(section.section_locations ?? []).map(currentPen => {
      const currentPenTerminals = terminals?.filter(t => t.fk_locations === currentPen.location_id);
      
      const motorTerminal = currentPenTerminals?.find(t => 
        t.fk_terminal_types === 80 || 
        t.fk_terminal_types === 81 || 
        t.fk_terminal_types === 50
      );
  
      const activatorTerminal = currentPenTerminals?.find(t => t.fk_terminal_types === 4);
      const waterTerminal = currentPenTerminals?.find(t => t.fk_terminal_types === 52);
  
      const motorDisabledArr = motorTerminal?.programmed?.toString(2).split("").reverse();
      const waterDisabledArr = waterTerminal?.programmed?.toString(2).split("").reverse();
      const activatorDisabledArr = activatorTerminal?.programmed?.toString(2).split("").reverse();

      return {
        locationName: {
          text: "Pen:",
          value: currentPen.name ?? ""
        },
        motor: motorTerminal && {
          input: false,
          terminal_id: motorTerminal.terminal_id,
          text: "Feeder Output", // TODO TRANSLATE
          port: motorTerminal.port ?? "",
          board: devices?.find(d => d.device_id === motorTerminal.fk_devices),
          disabled: !!(motorDisabledArr && motorDisabledArr.length >= 1 && motorDisabledArr[0] === "1"),
          programmed: motorTerminal.programmed ?? 0,
        },
        waterOutput: waterTerminal && {
          input: false,
          terminal_id: waterTerminal.terminal_id,
          text: "Water Output", // TODO TRANSLATE
          port: waterTerminal.port ?? "",
          board: devices?.find(d => d.device_id === waterTerminal.fk_devices),
          disabled: !!(waterDisabledArr && waterDisabledArr.length >= 1 && waterDisabledArr[0] === "1"),
          programmed: waterTerminal.programmed ?? 0,
        },
        activatorInput: activatorTerminal && {
          input: true,
          terminal_id: activatorTerminal.terminal_id,
          text: "Activator Input", // TODO TRANSLATE
          port: (activatorTerminal.port ?? 0) - 100,
          board: devices?.find(d => d.device_id === activatorTerminal.fk_devices),
          disabled: !!(activatorDisabledArr && activatorDisabledArr.length >= 1 && activatorDisabledArr[0] === "1"),
          programmed: activatorTerminal.programmed ?? 0,
        },
        visual: currentPen.visual,
        location_id: currentPen.location_id
      };
    });
  }, [section.section_locations, terminals, devices]);
  // #endregion

  const handleTimeout = () => {
    setTimeout(() => {
      dispatch({ error: { errorText: "" } });
    }, 2000)
  }
  // #region | Pen View
  const UpdateData = async () => {
    const sectionName = sectionChangeRef.current;
    const penNames = Array.from(Object.values(locationDirtRef.current));
    const terminalChanges = Array.from(Object.values(terminalDirtRef.current))

    sectionChangeRef.current = undefined;
    locationDirtRef.current = {};
    terminalDirtRef.current = {};

    if (sectionName !== undefined && String(sectionName).trim() === "") {
      dispatch({ error: { errorText: "section name missing" } }); // TODO TRANSLATE
      handleTimeout();
      return;
    }

    if (sectionName !== undefined || penNames.length || terminalChanges.length) {
      const response = await maintainSectionLocations(sectionName ? [{section_id: section.section_id, new_name: String(sectionName)}] : [], penNames, terminalChanges)
      
      if (!response) {
        dispatch({ error: { errorText: "edit failed" } }) // TODO TRANSLATE
        handleTimeout();
        return;
      }
      closeModal();
    }
  };

  const penView = useMemo(() => {
    if (!selectedPens) return null;

    return selectedPens.map((pen, index) => {
      if (!pen) return null;
  
      const data = penData.find(p => p.location_id === pen.location_id);
      if (!data) return null;
  
      const renderRow = (item: { text: string; port: string | number; board: DeviceInfo | undefined; disabled: boolean, terminal_id: number, input: boolean, programmed: number }, index: number) => {

        return (
          <div key={`row_${pen.location_id}_${index}`} style={{ display: "grid", gridTemplateColumns: "60px 60px 100px 43px", gap: "10px"}}>

            {/* Text */}
            <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
              <TooltipString contentString={item.text} duplicatedStrings overrides={{ contentstring: { ...fonts.fontWeight1 } }} /> 
            </div>

            {/* Port */}
            <NewInput
              key={`port_${pen.location_id}_${index}`}
              ref={el => {portRefs.current[item.terminal_id] = el}}
              placeholder={String(item.port)}
              onChange={(e) => {
                terminalDirtRef.current[item.terminal_id] = { ...terminalDirtRef.current[item.terminal_id], terminal_id: item.terminal_id, port: Number(e) + (item.input ? + 100 : 0) };
              }}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary,
                fieldType: NewFieldTypes.tertiaryField,
                fieldWidth: "60px"
              }}
              valueOverrides={{
                defaultValue: terminalDirtRef.current[item.terminal_id]?.port !== undefined ? terminalDirtRef.current[item.terminal_id].port! - (item.input ? 100 : 0) :item.port,
                valueType: "number",
                maxValueNumber: 99,
                minValueNumber: 1
              }}
            />

            {/* Board */}
            <NewSelect
              key={`board_${pen.location_id}_${item.board?.device_id}`}
              ref={el => { boardRefs.current[item.terminal_id] = el }}
              options={ioOptions}
              onChange={(selected) => {
                if (selected?.id === undefined) {
                  return 
                }
                terminalDirtRef.current[item.terminal_id] = {...terminalDirtRef.current[item.terminal_id], terminal_id: item.terminal_id, fk_devices: Number(selected.id) }
              }}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary,
                fieldType: NewFieldTypes.tertiaryField,
                fieldWidth: "100px"
              }}
              valueOverrides={{
                defaultValue: ioOptions?.find(io => io.id === (terminalDirtRef.current[item.terminal_id]?.fk_devices ?? item.board?.device_id))
              }}
            />

            {/* Disabled */}
            <ToggleBox
              toggleStyles={ToggleStyles.tableToggle}
              isChecked={item.disabled ? false : true}
              icon={glyphs.Start({size: "17px"})}
              onChange={(e) => {
                const newProgrammedArray = item.programmed.toString(2).split("").reverse()
                console.log(e);
                newProgrammedArray[0] = e ? "0" : "1";
                const configProgrammed = parseInt(newProgrammedArray.reverse().join(""), 2)
                terminalDirtRef.current[item.terminal_id] = {...terminalDirtRef.current[item.terminal_id], terminal_id: item.terminal_id, configProgrammed}
                if (item.input) {
                  if (data.motor === undefined) {
                    return
                  }
                  const programmed = terminalDirtRef.current[data.motor.terminal_id]?.configProgrammed ?? data.motor.programmed;
                  const motorProgrammedArray = programmed.toString(2).padStart(8,'0').split("").reverse();
                  if (Boolean(Number(motorProgrammedArray[2])) === !e) {
                    return
                  }
                  motorProgrammedArray[2] = e ? "0" : "1";
                  const motorProgrammed = parseInt(motorProgrammedArray.reverse().join(""),2)
                  terminalDirtRef.current[data.motor.terminal_id] = {...terminalDirtRef.current[data.motor.terminal_id], terminal_id: data.motor.terminal_id, configProgrammed: motorProgrammed}
                }
              }}
              />
          </div>
        )
      }
  
      return (
        <div
          key={`row_pen_${pen.location_id}_${index}`}
          style={{
            backgroundColor: colors.greyLightSecondary,
            padding: mobile ? "0px 10px" : "0px 20px",
            display: "flex",
            alignItems: "start",
            justifyContent: "center",
            flexDirection: "column",
            width: mobile ? "310px" : "333px",
            boxSizing: "border-box",
            borderRadius: "10px"
          }}
        >
          <div style={{ whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "5px", padding: "10px 0px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
            {data.locationName.text}

            <NewInput
              key={`name_${pen.location_id}_${index}`}
              ref={el => penNameRefs.current[pen.location_id] = el}
              onChange={(e) => {
                locationDirtRef.current[pen.location_id] = {location_id: pen.location_id, new_name: e}
              }}
              isRequired
              placeholder={""}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary,
                fieldType: NewFieldTypes.tertiaryField,
                fieldWidth: "100px"
              }}
              valueOverrides={{
                defaultValue: locationDirtRef.current[pen.location_id]?.new_name ?? pen.name,
                valueType: "number",
                minValueNumber: 1,
                maxValueNumber: 999999
              }}
            />
          </div>
          <div style={{ gridArea: "2/1/3/4", display: "flex", flexDirection: "column", borderTop: "2px solid " + colors.greyTertiary, padding: "10px 0px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "60px 60px 100px 43px", gap: "10px"}}>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary }}></p>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary, padding: "0px 5px" }}>Port</p>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary, padding: "0px 5px" }}>I/O Board</p>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary, padding: "0px 5px" }}>On</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              {[data.motor, data.waterOutput, data.activatorInput]
                .filter(Boolean)
                .map((item, i) => renderRow(item!, i))
              }
            </div>
          </div>
        </div>
      );
    });
  }, [selectedPens, ioOptions, penData]);
  // #endregion

  // #region | Popper Content
  const penPopper = useMemo(() => {
    return penData.map((pen, index) => {
      if (!pen) return null;
  
      const renderRow = (item: { text: string; port: string | number; board: DeviceInfo | undefined; disabled: boolean }, index: number) => (
        <div key={`popper_${pen.location_id}_${index}`} style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr 20px", gap: "10px"}}>
          
          {/* Text */}
          <p style={{...fonts.paragraph, ...fonts.fontSize4, ...fonts.fontWeight1, color: colors.greyLightTertiary}}>{item.text}:</p>

          {/* Port */}
          <p style={{...fonts.paragraph, ...fonts.fontSize4, ...fonts.fontWeight1, color: colors.greyLightTertiary}}>{item.port}</p>
          
          {/* Board */}
          <p style={{...fonts.paragraph, ...fonts.fontSize4, ...fonts.fontWeight1, color: colors.greyLightTertiary}}>{item.board?.name}</p>

          {/* Disabled */}
          {item.disabled === false ? <glyphs.Check fillColor={colors.systemGreen}/> : <glyphs.Minus fillColor={colors.systemRed}/>}
        </div>
      );
  
      return (
        <div key={`popper_pen_${pen.location_id}_${index}`} style={{ }}>
          
          <div style={{ gridArea: "2/1/3/4", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 40px 1fr 20px", gap: "10px"}}>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary }}></p>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary }}>Port</p>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary }}>I/O Board</p>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary }}>On</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {[pen.motor, pen.waterOutput, pen.activatorInput]
                  .filter(Boolean)
                  .map((item, i) => renderRow(item!, i))}
            </div>
          </div>
        </div>
      );
    });
  }, [penData]);
  // #endregion

  const getAttentionForPen = (locationId: number): 0 | 2 => {
    const hasLocationChange =
      locationId in locationDirtRef.current;
  
    const hasTerminalChange =
      Object.values(terminalDirtRef.current).some(
        (t) => t.terminal_id && terminals?.some(
          term =>
            term.terminal_id === t.terminal_id &&
            term.fk_locations === locationId
        )
      );
  
    return hasLocationChange || hasTerminalChange ? 2 : 0;
  };

  return (<>
      <NewModal
        modalState={true}
        overrides={{
          mainContentPadding: MainContentPaddingTypes.secondary,
          secondaryContentScroll: true,
          mainContentScroll: true
        }}
        predefinedButtons={{
          onClose: () => {
            sectionChangeRef.current = undefined;
            locationDirtRef.current = {};
            terminalDirtRef.current = {};
            closeModal()
          },
        }}
        header={t("locationFeederPages.sections.editSection", { defaultValue: "edit section" })}
        subHeader={section.name}
        mainContent={ selectedSection ? (
          <div style={{ minHeight: mobile ? "100%" : undefined, height: mobile ? undefined : "100%", boxSizing: "border-box", gap: mobile ? "20px 0px" : "20px", display: "grid", gridTemplateColumns: mobile ? "1fr" : "350px 2px 280px", gridTemplateRows: mobile ? "fit-content(100%) 2px fit-content(100%)" : "100%" }}>
            
            {/* Edit Area */}
            <div style={{ gridArea: "1/1/2/2", height: "100%", display: "grid", gap: "10px", gridTemplateColumns: "100%", gridTemplateRows: mobile ? "60px 2px 250px 2px" : "50px 2px 1fr 2px" }}>
  
              {/* Section Name */}
              <div style={{ gridArea: "1/1/2/2", margin: "0 auto", boxSizing: "border-box", width: "100%", display: "flex", alignItems: "end", height: "44px", justifyContent: "space-between" }}>
                <NewInput
                  ref={sectionNameRef}
                  key={`name_${section.section_id}`}
                  header={t("locationFeederPages.sections.sectionName", { defaultValue: "section name" })}
                  onChange={(e) => {sectionChangeRef.current = e}}
                  isRequired
                  valueOverrides={{
                    defaultValue: section.name
                  }}
                  
                />
                <NewButton
                  content=""
                  buttonType={ButtonTypes.glyphButton}
                  startEnhancer={glyphs.Delete({ size: "25px" })}
                  overrides={{ startEnhancerStyles: { height: "30px", display: "flex", alignItems: "center" } }}
                  onClick={() => setDeleteModal(true)}
                />
              </div>
      
              {/* Seperator */}
              <div style={{ gridArea: "2/1/3/2", borderBottom: "2px solid " + colors.blueDarkSecondary, width: mobile ? "80%" : undefined, margin: mobile ? "0 auto" : undefined }} />

              {/* Active Pen List */}
              <div style={{ gridArea: "3/1/4/2", height: "100%", overflow: "hidden", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "5px", overflowX: "hidden", overflowY: "auto", alignItems: "center", scrollbarColor: colors.blueDarkSecondary+colors.transparent}}>
                {(selectedPens === undefined || selectedPens.length === 0) && 
                  <p style={{ ..._fontSize, ...css.emptyPen, color: colors.greyLightTertiary, opacity: "0.5" }}> {`${t("systemControls.general.noPensSelected", { defaultValue: "No pens selected" })}!`} </p>
                }
                {selectedPens && <>{penView}</> }
              </div>
              
              {/* Seperator */}
              <div style={{ gridArea: "4/1/5/2", borderBottom: "2px solid " + colors.blueDarkSecondary, width: mobile ? "80%" : undefined, margin: mobile ? "0 auto" : undefined }} />
            
            </div>
      
            {/* Seperator */}
            <div style={{ gridArea: mobile ? "2/1/3/2" : "1/2/2/3", borderLeft: mobile ? undefined : "2px solid " + colors.blueDarkSecondary, borderBottom: mobile ? "2px solid " + colors.blueDarkSecondary : undefined }} />
      
            {/* Pens */}
            <div style={{ gridArea: mobile ? "3/1/4/3" : "1/3/2/4", height: "100%", width: "100%", display: "grid", gap: "5px", gridTemplateRows: mobile ? "30px 2px 250px 2px" : "22px 2px 1fr 2px", gridTemplateColumns: "100%" }}>
      
              {/* Select Text */}
              <p style={{ gridArea: "1/1/2/2", ...fonts.paragraph, ..._fontSize, color: colors.greyLightTertiary, textAlign: "center" }}> {`${t("locationFeederPages.sections.clickPen", { defaultValue: "Click every pen you want to edit" })}:`} </p>
      
              <div style={{ gridArea: "2/1/3/2", borderBottom: "2px solid " + colors.blueDarkSecondary, width: mobile ? "80%" : undefined, margin: mobile ? "0 auto" : undefined }} />
              
              {/* Section */}
              <div style={{
                gridArea: "3/1/4/2",
                transition: "filter 0.3s ease-in-out",
                overflowX: "hidden",
                overflowY: "auto",
                scrollbarColor: colors.blueDarkSecondary + colors.transparent,
                scrollbarWidth: "thin",
                display: "flex",
                padding: "10px",
                borderRadius: "10px",
                justifyContent: "center",
                alignItems: "flex-start"
              }}>
                <div style={{display: "block", margin: "auto"}}>
                  <LF_PENLIST
                    key={selectedSection.section_id}
                    focus_view={false}
                    section={selectedSection}
                    popoverContent={(index) => (
                      <div style={{}}>
                        {penPopper[index]}
                      </div>
                    )}
                    overrides={(index) => {
                      const locationId = selectedSection.section_locations[index]?.location_id;
                    
                      return {
                        primaryColor: colors.greyLightSecondary,
                        fittedWidth: true,
                        attention: locationId ? getAttentionForPen(locationId) : undefined
                      }
                    }}
                  />
                </div>
              </div>

              <div style={{ gridArea: "4/1/5/2", borderBottom: "2px solid " + colors.blueDarkSecondary, width: mobile ? "80%" : undefined, margin: mobile ? "0 auto" : undefined }} />
      
            </div>
      
          </div> ) : (<></>) }
        footerContent={
          <NewButton 
            content={t("buttons.save", {defaultValue: "save"})}
            onClick={UpdateData}
          />
        }
        footerMessage={error.errorText}
      />
    
    {deleteModal && 
      <ConfirmModal
        disclaimer={`${t("locationFeederPages.sections.deleteSectionConfirm", { defaultValue: "Are you sure you want to delete this section" })}?`}
        onCancel={() => setDeleteModal(false)}
        onConfirm={async () => {
          setDeleteModal(false);
          const response = await deleteSectionLocations(section.section_id)
      
          if (!response) {
            dispatch({ error: { errorText: "delete failed" } }) // TODO TRANSLATE
            handleTimeout();
            return;
          }
          closeModal();
          
          // TODO delete section
        }}
      /> 
    }
  </>);
}